import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { userService, storageService } from '../services/supabase';
import { FiSave, FiX, FiUpload, FiArrowLeft } from 'react-icons/fi';

interface UserFormData {
  nome: string;
  email: string;
  password?: string;  // Marcando password como opcional
  tipo_usuario: 'admin' | 'comum';
  avatar_url: string;
}

const UserForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState<UserFormData>({
    nome: '',
    email: '',
    password: '',
    tipo_usuario: 'comum',
    avatar_url: ''
  });
  
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode) {
      fetchUser();
    }
  }, [id]);

  const fetchUser = async () => {
    try {
      setFetchLoading(true);
      const { data, error } = await userService.getUserById(id!);
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (!data) {
        throw new Error('Usuário não encontrado');
      }
      
      setFormData({
        nome: data.nome,
        email: data.email,
        password: '',
        tipo_usuario: data.tipo_usuario,
        avatar_url: data.avatar_url || ''
      });
      
      if (data.avatar_url) {
        setAvatarPreview(data.avatar_url);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados do usuário');
      console.error('Erro ao buscar usuário:', err);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAvatarFile(file);
      
      // Criar preview do avatar
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      let avatarUrl = formData.avatar_url;
      
      // Upload do novo avatar (se houver)
      if (avatarFile) {
        const userId = isEditMode ? id! : 'temp-' + Date.now(); // ID temporário para novos usuários
        const { data: uploadData, error: uploadError } = await storageService.uploadAvatar(avatarFile, userId);
        
        if (uploadError) {
          throw new Error('Erro ao fazer upload do avatar: ' + uploadError.message);
        }
        
        avatarUrl = uploadData.publicUrl;
      }
      
      // Dados do usuário para envio
      const userData: UserFormData = {
        ...formData,
        avatar_url: avatarUrl
      };
      
      let response;
      
      if (isEditMode) {
        // Remove a senha se não foi alterada (campo vazio)
        if (!userData.password) {
          delete userData.password;
        }
        
        response = await userService.updateUser(id!, userData);
      } else {
        // Verificar se a senha está preenchida para novo usuário
        if (!userData.password) {
          throw new Error('A senha é obrigatória para novos usuários');
        }
        
        response = await userService.createUser(userData);
      }
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      setSuccess(true);
      
      // Redirecionar após sucesso
      setTimeout(() => {
        navigate('/usuarios');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar usuário');
      console.error('Erro ao salvar usuário:', err);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-vermelho-claro"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-6 flex items-center">
        <Link to="/usuarios" className="mr-4 text-gray-400 hover:text-white transition-colors">
          <FiArrowLeft size={20} />
        </Link>
        <h1 className="text-2xl md:text-3xl font-bold text-white font-raleway">
          {isEditMode ? 'Editar' : 'Novo'} <span className="text-vermelho-claro">Usuário</span>
        </h1>
      </div>
      
      {error && (
        <div className="bg-red-900/30 border border-red-800 rounded-md p-3 mb-6 flex items-center text-red-200 animate-fade-in">
          <FiX className="mr-2 flex-shrink-0 text-red-400" />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-900/30 border border-green-800 rounded-md p-3 mb-6 flex items-center text-green-200 animate-fade-in">
          <FiSave className="mr-2 flex-shrink-0 text-green-400" />
          <span>Usuário {isEditMode ? 'atualizado' : 'criado'} com sucesso! Redirecionando...</span>
        </div>
      )}
      
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Coluna da esquerda - Informações pessoais */}
            <div className="space-y-6">
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-300 mb-1">
                  Nome completo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  required
                  value={formData.nome}
                  onChange={handleChange}
                  className="input"
                  placeholder="Nome do usuário"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="input"
                  placeholder="email@exemplo.com"
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                  Senha {!isEditMode && <span className="text-red-500">*</span>}
                  {isEditMode && <span className="text-gray-500 text-xs ml-2">(Deixe em branco para manter a senha atual)</span>}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  required={!isEditMode}
                  value={formData.password}
                  onChange={handleChange}
                  className="input"
                  placeholder="••••••••"
                />
              </div>
              
              <div>
                <label htmlFor="tipo_usuario" className="block text-sm font-medium text-gray-300 mb-1">
                  Tipo de usuário <span className="text-red-500">*</span>
                </label>
                <select
                  id="tipo_usuario"
                  name="tipo_usuario"
                  required
                  value={formData.tipo_usuario}
                  onChange={handleChange}
                  className="input bg-gray-800"
                >
                  <option value="comum">Usuário Comum</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>
            
            {/* Coluna da direita - Avatar */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Avatar do usuário
              </label>
              
              <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-700 rounded-lg">
                {avatarPreview ? (
                  <div className="mb-4">
                    <div className="relative w-32 h-32 rounded-full overflow-hidden">
                      <img
                        src={avatarPreview}
                        alt="Preview do avatar"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setAvatarPreview(null);
                          setAvatarFile(null);
                          setFormData(prev => ({ ...prev, avatar_url: '' }));
                        }}
                        className="absolute top-0 right-0 bg-red-600 text-white p-1 rounded-full"
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="w-32 h-32 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    <span className="text-gray-400 text-4xl font-bold">
                      {formData.nome ? formData.nome.substring(0, 2).toUpperCase() : 'U'}
                    </span>
                  </div>
                )}
                
                <label htmlFor="avatar" className="btn btn-secondary">
                  <FiUpload className="mr-2" />
                  {avatarPreview ? 'Trocar Avatar' : 'Fazer Upload'}
                </label>
                <input
                  type="file"
                  id="avatar"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
                
                <p className="mt-2 text-xs text-gray-500">
                  Formatos suportados: JPG, PNG, GIF (max. 2MB)
                </p>
              </div>
            </div>
          </div>
          
          {/* Botões de ação */}
          <div className="mt-8 flex justify-end space-x-4">
            <Link
              to="/usuarios"
              className="btn btn-secondary"
            >
              <FiX className="mr-2" />
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={loading}
              className={`btn btn-primary ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
              ) : (
                <FiSave className="mr-2" />
              )}
              {isEditMode ? 'Atualizar' : 'Salvar'} Usuário
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default UserForm; 